import * as React from 'react';
import { Download, GripVertical, ChevronRight, ChevronLeft } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { exportToXlsx, fileTimestamp, type ExcelCellValue } from '@/lib/export-excel';
import { fetchJson, type OrgSummary } from '@/lib/api';

const APP_NAME = 'Easy Call';

export interface DataTableColumn<T> {
  /** Stable id for the column. */
  key: string;
  /** Header content shown in the UI. */
  header: React.ReactNode;
  /** Cell content shown in the UI. */
  cell: (row: T) => React.ReactNode;
  /**
   * Value used when exporting to Excel. If omitted, a string/number returned by
   * `cell` is used; anything else (JSX) exports as empty.
   */
  exportValue?: (row: T) => ExcelCellValue;
  /** Header text for the Excel file. Defaults to `header` when it is a string. */
  exportHeader?: string;
  /** Set false to omit this column from the Excel export (e.g. an actions column). */
  exportable?: boolean;
  align?: 'start' | 'center' | 'end';
  className?: string;
  headerClassName?: string;
  /** Stop row-click propagation for this cell (e.g. action buttons). */
  stopClickPropagation?: boolean;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowKey: (row: T, index: number) => string;
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  /** Extra controls rendered on the start side of the toolbar. */
  toolbar?: React.ReactNode;
  /** Extra controls rendered on the end side of the toolbar (next to export). */
  toolbarActions?: React.ReactNode;
  /** Enable the built-in client-side Excel export with the given file name. */
  exportFileName?: string;
  exportSheetName?: string;
  /** Enable client-side pagination with a show-all / paged toggle. */
  pagination?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  /** Enable drag-to-reorder. Receives the new id order. Disables pagination. */
  dnd?: { onReorder: (orderedIds: string[]) => void };
  className?: string;
}

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ALIGN_CLASS: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  start: 'text-start',
  center: 'text-center',
  end: 'text-end'
};

function exportHeaderText<T>(col: DataTableColumn<T>): string {
  if (col.exportHeader !== undefined) return col.exportHeader;
  if (typeof col.header === 'string') return col.header;
  return col.key;
}

function SortableRow<T>({
  rowKey,
  row,
  columns,
  onRowClick,
  rowClassName
}: {
  rowKey: string;
  row: T;
  columns: DataTableColumn<T>[];
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rowKey
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        onRowClick && 'cursor-pointer',
        isDragging && 'bg-accent/30',
        rowClassName?.(row)
      )}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      <TableCell className="w-10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-muted-foreground hover:text-foreground"
          aria-label="גרור לסידור מחדש"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      {columns.map((col) => (
        <TableCell
          key={col.key}
          className={cn(col.align && ALIGN_CLASS[col.align], col.className)}
          onClick={col.stopClickPropagation ? (e) => e.stopPropagation() : undefined}
        >
          {col.cell(row)}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function DataTable<T>({
  columns,
  data,
  getRowKey,
  loading,
  emptyMessage = 'אין נתונים להצגה.',
  onRowClick,
  rowClassName,
  toolbar,
  toolbarActions,
  exportFileName,
  exportSheetName,
  pagination,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  dnd,
  className
}: DataTableProps<T>) {
  const paginationEnabled = !!pagination && !dnd;
  const [showAll, setShowAll] = React.useState(false);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const [page, setPage] = React.useState(1);

  const total = data.length;
  const pageCount = paginationEnabled && !showAll ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  // Clamp page when data shrinks.
  React.useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const visible = React.useMemo(() => {
    if (!paginationEnabled || showAll) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, paginationEnabled, showAll, page, pageSize]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const rowIds = React.useMemo(
    () => data.map((row, i) => getRowKey(row, i)),
    [data, getRowKey]
  );

  function handleDragEnd(event: DragEndEvent) {
    if (!dnd) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    dnd.onReorder(arrayMove(rowIds, oldIndex, newIndex));
  }

  const [exporting, setExporting] = React.useState(false);

  async function handleExport() {
    if (!exportFileName) return;
    const exportCols = columns.filter((c) => c.exportable !== false);
    const excelColumns = exportCols.map((c) => ({ header: exportHeaderText(c), key: c.key }));
    const rows = data.map((row) => {
      const out: Record<string, ExcelCellValue> = {};
      for (const c of exportCols) {
        if (c.exportValue) {
          out[c.key] = c.exportValue(row);
        } else {
          const node = c.cell(row);
          out[c.key] = typeof node === 'string' || typeof node === 'number' ? node : '';
        }
      }
      return out;
    });
    setExporting(true);
    try {
      const org = await fetchJson<OrgSummary>('/api/me/active-org').catch(() => null);
      const now = new Date();
      const fileName = [exportFileName, org?.slug, fileTimestamp(now)].filter(Boolean).join('-');
      await exportToXlsx({
        fileName,
        sheetName: exportSheetName,
        columns: excelColumns,
        rows,
        meta: {
          appName: APP_NAME,
          orgName: org?.name,
          orgSlug: org?.slug,
          generatedAt: now
        }
      });
    } catch {
      toast.error('שגיאה בייצוא לאקסל');
    } finally {
      setExporting(false);
    }
  }

  const showToolbar = toolbar || toolbarActions || exportFileName;
  const colSpan = columns.length + (dnd ? 1 : 0);
  const stickyHead =
    'sticky top-0 z-10 whitespace-nowrap bg-card shadow-[inset_0_-1px_0_hsl(var(--border))]';

  const body = (
    <table className="w-full caption-bottom text-sm">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {dnd && <TableHead className={cn(stickyHead, 'w-10')} />}
          {columns.map((col) => (
            <TableHead
              key={col.key}
              className={cn(stickyHead, col.align && ALIGN_CLASS[col.align], col.headerClassName)}
            >
              {col.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {visible.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={colSpan} className="py-8 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : dnd ? (
          <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
            {visible.map((row, i) => (
              <SortableRow
                key={getRowKey(row, i)}
                rowKey={getRowKey(row, i)}
                row={row}
                columns={columns}
                onRowClick={onRowClick}
                rowClassName={rowClassName}
              />
            ))}
          </SortableContext>
        ) : (
          visible.map((row, i) => (
            <TableRow
              key={getRowKey(row, i)}
              className={cn(onRowClick && 'cursor-pointer', rowClassName?.(row))}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(col.align && ALIGN_CLASS[col.align], col.className)}
                  onClick={col.stopClickPropagation ? (e) => e.stopPropagation() : undefined}
                >
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </table>
  );

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-3', className)}>
      {showToolbar && (
        <div className="flex flex-none flex-wrap items-center gap-2">
          {toolbar}
          <div className="ms-auto flex items-center gap-2">
            {toolbarActions}
            {exportFileName && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={total === 0 || exporting}
              >
                <Download className="h-4 w-4" />
                {exporting ? 'מייצא...' : 'ייצא לאקסל'}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded-md border">
        {loading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : dnd ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            {body}
          </DndContext>
        ) : (
          body
        )}
      </div>

      {(paginationEnabled || total > 0) && (
        <div className="flex flex-none flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>{total.toLocaleString('he-IL')} שורות</span>
          {paginationEnabled && (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAll((s) => !s);
                  setPage(1);
                }}
              >
                {showAll ? 'הצג בעמודים' : 'הצג הכל'}
              </Button>
              {!showAll && (
                <>
                  <select
                    className="h-8 rounded-md border bg-transparent px-2 text-sm"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    aria-label="שורות בעמוד"
                  >
                    {pageSizeOptions.map((n) => (
                      <option key={n} value={n}>
                        {n} בעמוד
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      aria-label="הקודם"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <span className="min-w-[5rem] text-center">
                      עמוד {page} מתוך {pageCount}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      disabled={page >= pageCount}
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      aria-label="הבא"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
